import playerjs from 'player.js';
import { useEffect, useRef } from 'react';

export interface PlaybackPayload {
  embedUrl: string;
  expires?: number;
}

/**
 * The vendor seam. Renders the token-authed Bunny embed (an iframe) from the
 * server-minted `playback` payload and drives it through player.js — Bunny's
 * supported postMessage protocol. The page never touches the vendor shape: it
 * passes plain callbacks (position as numbers) and a plain `resumeAt` number,
 * and receives nothing vendor-specific back.
 *
 * The native-HLS escape hatch (swap this iframe for a <video> + hls.js against a
 * signed .m3u8) still lives ONLY here — a swap would re-implement these same
 * callbacks plus a `.currentTime =` seek.
 */
export function PlayerSurface({
  playback,
  title,
  onPlay,
  onTimeUpdate,
  onEnded,
  resumeAt,
}: {
  playback: PlaybackPayload;
  title: string;
  onPlay?: () => void;
  /** Fired frequently while playing (Bunny's `timeupdate`); the page throttles. */
  onTimeUpdate?: (positionSeconds: number, durationSeconds: number) => void;
  onEnded?: () => void;
  /** Seconds to seek to once the player is ready (resume-on-reopen). */
  resumeAt?: number;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Latest callbacks/resumeAt in a ref so the player initializes once per mount
  // and never re-attaches when a prop identity changes.
  const cbRef = useRef({ onPlay, onTimeUpdate, onEnded, resumeAt });
  cbRef.current = { onPlay, onTimeUpdate, onEnded, resumeAt };

  useEffect(() => {
    const el = iframeRef.current;
    if (!el) return;

    let disposed = false;
    const player = new playerjs.Player(el);

    player.on('play', () => {
      if (!disposed) cbRef.current.onPlay?.();
    });
    player.on('timeupdate', (data) => {
      if (disposed) return;
      // player.js delivers an object; some builds send a JSON string — accept both.
      const payload =
        typeof data === 'string'
          ? (JSON.parse(data) as { seconds?: number; duration?: number })
          : data;
      const seconds = Number(payload?.seconds);
      const duration = Number(payload?.duration);
      if (Number.isFinite(seconds) && Number.isFinite(duration) && duration > 0) {
        cbRef.current.onTimeUpdate?.(seconds, duration);
      }
    });
    player.on('ended', () => {
      if (!disposed) cbRef.current.onEnded?.();
    });
    player.on('ready', () => {
      if (disposed) return;
      // Seek after ready but before playback settles — no visible jump.
      const at = cbRef.current.resumeAt;
      if (at && at > 0) {
        try {
          player.setCurrentTime(at);
        } catch {
          /* vendor no-op — resume is best-effort */
        }
      }
    });

    return () => {
      disposed = true;
      try {
        player.off('play');
        player.off('timeupdate');
        player.off('ended');
        player.off('ready');
      } catch {
        /* player.js has no destroy(); off() is best-effort teardown */
      }
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={playback.embedUrl}
      title={title}
      className="absolute inset-0 h-full w-full"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
      allowFullScreen
    />
  );
}
